import React, { createContext, useContext, useState, useCallback } from 'react';
import { siteService } from '../services/api';

const SiteContext = createContext();

export const useSite = () => {
  const context = useContext(SiteContext);
  if (!context) {
    throw new Error('useSite must be used within a SiteProvider');
  }
  return context;
};

export const SiteProvider = ({ children }) => {
  const [sites, setSites] = useState([]);
  const [currentSite, setCurrentSite] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchSites = useCallback(async () => {
    try {
      setLoading(true);
      const response = await siteService.getSites();
      // Handle new response structure with sites and planInfo
      if (response.data && Array.isArray(response.data.sites)) {
        setSites(response.data.sites);
      } else if (Array.isArray(response.data)) {
        // Fallback for old structure
        setSites(response.data);
      } else {
        setSites([]);
      }
    } catch (error) {
      console.error('Failed to fetch sites:', error);
      setSites([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchSite = useCallback(async (siteId) => {
    try {
      setLoading(true);
      const response = await siteService.getSite(siteId);
      setCurrentSite(response.data);
      return response.data;
    } catch (error) {
      console.error('Failed to fetch site:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  const createSite = useCallback(async (siteData) => {
    try {
      setLoading(true);
      const response = await siteService.createSite(siteData);
      setSites(prev => [response.data, ...prev]);
      return response.data;
    } catch (error) {
      console.error('Failed to create site:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  const updateSite = useCallback(async (siteId, updates) => {
    try {
      const response = await siteService.updateSite(siteId, updates);
      
      // Update current site if it's the one being updated
      if (currentSite && currentSite._id === siteId) {
        setCurrentSite(response.data);
      }
      
      // Update sites list
      setSites(prev => prev.map(site => 
        site._id === siteId ? response.data : site
      ));
      
      return response.data;
    } catch (error) {
      console.error('Failed to update site:', error);
      throw error;
    }
  }, [currentSite]);

  const deleteSite = useCallback(async (siteId) => {
    try {
      await siteService.deleteSite(siteId);
      setSites(prev => prev.filter(site => site._id !== siteId));
      
      if (currentSite && currentSite._id === siteId) {
        setCurrentSite(null);
      }
    } catch (error) {
      console.error('Failed to delete site:', error);
      throw error;
    }
  }, [currentSite]);

  const publishSite = useCallback(async (siteId) => {
    try {
      const response = await siteService.publishSite(siteId);
      
      // Update sites list
      setSites(prev => prev.map(site => 
        site._id === siteId ? { ...site, status: 'published' } : site
      ));
      
      return response.data;
    } catch (error) {
      console.error('Failed to publish site:', error);
      throw error;
    }
  }, []);

  const value = {
    sites,
    currentSite,
    loading,
    fetchSites,
    fetchSite,
    createSite,
    updateSite,
    deleteSite,
    publishSite,
    setCurrentSite
  };

  return (
    <SiteContext.Provider value={value}>
      {children}
    </SiteContext.Provider>
  );
};